import { useDataQuery } from "@hooks/useData/useDataQuery";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import { selectData, supabase, softDelete } from "@utils/serverClient";
import { TemplateCategory, InsertTemplateCategory, UpdateTemplateCategory } from "types";

export function useTemplateCategories() {
  const queryKey = ["template_categories"];

  const { data, status, refetch } = useDataQuery<TemplateCategory[]>({
    queryKey: queryKey,
    queryFn: async () => {
      const { data, error } = await selectData({
        table: "template_category",
      });
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useDataMutation({
    mutationFn: async (formData: InsertTemplateCategory) => {
      const { data, error } = await supabase
        .from("template_category")
        .insert(formData)
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to add template category.",
    optimisticUpdateFn: (oldData: TemplateCategory[] | undefined, newCategory) => {
      const optimisticCategory: TemplateCategory = {
        id: Math.random(), // Temporary ID
        deleted: false,
        updated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        ...newCategory,
        name: newCategory.name ?? null,
      };
      return [optimisticCategory, ...(oldData || [])];
    },
  });

  const updateMutation = useDataMutation({
    mutationFn: async (formData: UpdateTemplateCategory) => {
      const { data, error } = await supabase
        .from("template_category")
        .update({ name: formData.name })
        .match({ id: formData.id })
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update template category.",
    optimisticUpdateFn: (oldData: TemplateCategory[] | undefined, updatedCategory) => {
      if (!oldData) return [];
      return oldData.map((category) =>
        category.id === updatedCategory.id ? { ...category, ...updatedCategory } : category,
      );
    },
  });

  const deleteMutation = useDataMutation({
    mutationFn: async (id) => {
      const { error } = await softDelete({
        table: "template_category",
      }).match({ id });
      if (error) throw new Error(error.message);
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete template category.",
    optimisticUpdateFn: (oldData: TemplateCategory[] | undefined, id) => {
      return oldData?.filter((category) => category.id !== id) ?? [];
    },
  });

  return {
    templateCategories: data ?? [],
    templateCategoriesFetchStatus: status,
    getTemplateCategories: refetch,
    createTemplateCategory: createMutation.mutate,
    isCreatingTemplateCategory: createMutation.isPending,
    updateTemplateCategory: updateMutation.mutate,
    isUpdatingTemplateCategory: updateMutation.isPending,
    deleteTemplateCategory: deleteMutation.mutate,
    isDeletingTemplateCategory: deleteMutation.isPending,
  };
}
